import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { getEvaluators, getEvaluatorStats, getSubjects } from "../../services/questionService";
import Select from "../../components/forms/Select/Select";
import { filterSelectStyles } from "../../components/forms/Select/styles";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { focusRing, useMediaQuery } from "../Exams/TestBuilder/ui";
import { isCanceledError } from "../../utils/examError";
import { HelpTitle } from "../../components/Help/HelpButton";
import EvaluatorsGuide from "../../components/Help/guides/EvaluatorsGuide";
import Table, { EvaluatorCards } from "./Table";
import Modal from "./Modal";

const PAGE_SIZE = 50;
// The list follows the search box after this pause in typing.
const SEARCH_DELAY = 350;

// The subjects come as a plain list, other lists as a page ({results}).
const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);

// The list's orders: `ordering` is the API's (the name breaks ties), `column` + `direction` mark the sorted
// column of the table. The server sorts every page, so the rows are shown in its order.
const SORTS = [
  {
    value: "reviews_desc",
    label: "evaluators.sort_options.reviews_desc",
    ordering: "-total_reviews,first_name,last_name",
    column: "reviews",
    direction: "descending",
  },
  {
    value: "reviews_asc",
    label: "evaluators.sort_options.reviews_asc",
    ordering: "total_reviews,first_name,last_name",
    column: "reviews",
    direction: "ascending",
  },
  {
    value: "name_asc",
    label: "evaluators.sort_options.name_asc",
    ordering: "first_name,last_name",
    column: "evaluator",
    direction: "ascending",
  },
  {
    value: "upvotes_desc",
    label: "evaluators.sort_options.upvotes_desc",
    ordering: "-upvotes,first_name,last_name",
    column: "upvotes",
    direction: "descending",
  },
  {
    value: "downvotes_desc",
    label: "evaluators.sort_options.downvotes_desc",
    ordering: "-downvotes,first_name,last_name",
    column: "downvotes",
    direction: "descending",
  },
];
// The API's own order: most reviews first.
const DEFAULT_SORT = "reviews_desc";

const STAT_TILES = [
  { key: "total_evaluators", label: "evaluators.stats.total_evaluators", Icon: UsersIcon },
  { key: "active_this_month", label: "evaluators.stats.active_this_month", Icon: UserIcon },
  { key: "total_reviews", label: "evaluators.stats.total_reviews", Icon: ClipboardDocumentListIcon },
  { key: "total_reviews_this_month", label: "evaluators.stats.reviews_this_month", Icon: ChartBarIcon },
];
// The totals share one card with thin lines between them: a short list on phones (the number at the end of its
// line), two columns from sm up, four in a row from xl up.
const STAT_LINES = ["", "border-t sm:border-l sm:border-t-0", "border-t xl:border-l xl:border-t-0", "border-t sm:border-l xl:border-t-0"];

const secondaryButton = `mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;
const messageCard = "flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm";

export default function Evaluators() {
  const [t] = useTranslation();
  // Phones get the list as cards instead of the table.
  const wide = useMediaQuery("(min-width: 768px)");
  // The bottom pager brings the top of the list back into view.
  const resultsRef = useRef(null);

  // null while loading (the tiles show a dash instead of zeros).
  const [stats, setStats] = useState(null);
  const [subjects, setSubjects] = useState([]);

  // The search box shows what is typed; the list follows `search` after a pause.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("all");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  const [evaluators, setEvaluators] = useState([]);
  // null until the first answer, so the count does not start at "0".
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvaluator, setSelectedEvaluator] = useState(null);

  const currentSort = SORTS.find((s) => s.value === sort) || SORTS[0];
  const ordering = currentSort.ordering;

  // The stats and the subjects of the filter, once.
  useEffect(() => {
    const controller = new AbortController();
    getEvaluatorStats({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((data) => setStats(data || null))
      .catch((err) => {
        if (!isCanceledError(err)) console.error("Failed to fetch evaluator stats", err);
      });
    getSubjects({ params: { page: 1, page_size: 500 }, signal: controller.signal })
      .then((data) => setSubjects(listOf(data)))
      .catch((err) => {
        if (!isCanceledError(err)) console.error("Failed to fetch subjects", err);
      });
    return () => controller.abort();
  }, []);

  // The typed search applies after a pause in typing, from the first page.
  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // One page of experts; a newer request (another page, filter or order) aborts the older one.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    getEvaluators({
      params: {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        domain_subject: subject !== "all" ? subject : undefined,
        ordering,
      },
      signal: controller.signal,
    })
      .then((data) => {
        setEvaluators(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        // A page that no longer exists (fewer experts than before): back to the first one.
        if (err?.response?.status === 404 && page > 1) {
          setPage(1);
          return;
        }
        console.error("Failed to fetch evaluators", err);
        setEvaluators([]);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [page, search, subject, ordering, reloadKey]);

  const rows = evaluators;

  const subjectOptions = useMemo(
    () => [
      { value: "all", label: t("evaluators.filters.all_subjects") },
      ...subjects.map((s) => ({ value: s.uuid, label: s.name })),
    ],
    [subjects, t]
  );
  const sortOptions = SORTS.map(({ value, label }) => ({ value, label: t(label) }));

  const filtersActive = !!searchInput.trim() || subject !== "all";
  // The list shown answers the filters applied (the search applies after its pause): its empty state follows them.
  const listFiltered = !!search || subject !== "all";
  const pageCount = Math.ceil((count || 0) / PAGE_SIZE);

  const changeSubject = (value) => {
    setSubject(value);
    setPage(1);
  };
  const changeSort = (value) => {
    setSort(value);
    setPage(1);
  };
  // Every filter back to "all" at once (no search pause); the order stays.
  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setSubject("all");
    setPage(1);
  };
  const pageFromBottom = (target) => {
    setPage(target);
    resultsRef.current?.scrollIntoView({ block: "start" });
  };

  const handleViewEvaluator = (evaluator) => {
    setSelectedEvaluator(evaluator);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvaluator(null);
  };

  const filters = [
    {
      key: "subject",
      active: subject !== "all",
      node: (
        <Select
          customStyles={filterSelectStyles}
          options={subjectOptions}
          value={subject}
          onChange={(option) => changeSubject(option?.value || "all")}
          placeholder={t("evaluators.filters.all_subjects")}
          isSearchable
        />
      ),
    },
  ];

  const EmptyIcon = listFiltered ? MagnifyingGlassIcon : UsersIcon;

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <div>
        {/* The guide's "?" right after the title. */}
        <HelpTitle helpId="evaluators" helpTitle={t("help.evaluators.title")} guide={<EvaluatorsGuide />}>
          {t("nav.evaluators")}
        </HelpTitle>
        <p className="mt-1 text-sm text-slate-500">{t("evaluators.subtitle")}</p>
      </div>

      {/* Neutral numbers, a zero greyed (a dash while they load). */}
      <dl className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:grid-cols-2 xl:grid-cols-4">
        {STAT_TILES.map(({ key, label, Icon }, index) => {
          const value = stats ? stats[key] ?? 0 : null;
          return (
            <div
              key={key}
              className={`flex min-w-0 items-center justify-between gap-3 border-slate-100 px-4 py-3 sm:block sm:px-5 sm:py-4 ${STAT_LINES[index]}`}
            >
              <dt className="flex min-w-0 items-start gap-1.5 text-sm text-slate-500">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="min-w-0">{t(label)}</span>
              </dt>
              <dd
                className={`shrink-0 text-lg font-semibold tabular-nums sm:mt-1 sm:text-2xl sm:tracking-tight ${
                  value ? "text-slate-900" : "text-slate-500"
                }`}
              >
                {value === null ? "—" : value}
              </dd>
            </div>
          );
        })}
      </dl>

      <FilterPanel
        search={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t("evaluators.search_placeholder")}
        searchLabel={t("evaluators.search_label")}
        filters={filters}
        canClear={filtersActive}
        onClearAll={clearFilters}
      />

      {!failed && (
        <div ref={resultsRef} className="scroll-mt-4">
          <ResultsBar
            countText={count === null ? " " : t("evaluators.list.count", { count })}
            page={page}
            pageCount={pageCount}
            ordering={sort}
            onOrderingChange={changeSort}
            sortOptions={sortOptions}
            onPage={setPage}
            loading={loading}
          />
        </div>
      )}

      <div className="min-w-0">
        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
            <div className="text-center">
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
              <p className="text-sm text-slate-500">{t("evaluators.loading_evaluators")}</p>
            </div>
          </div>
        ) : failed ? (
          <div className={messageCard}>
            <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
              <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <p role="alert" className="text-sm font-medium text-slate-900">
              {t("evaluators.list.load_failed")}
            </p>
            <button type="button" onClick={() => setReloadKey((key) => key + 1)} className={secondaryButton}>
              {t("evaluators.list.retry")}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className={messageCard}>
            <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
              <EmptyIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <h3 className="text-sm font-medium text-slate-900">{t("evaluators.no_evaluators")}</h3>
            {/* "Clear all" is the filters card's, right above: not repeated here. */}
            <p className="mt-1 text-sm text-slate-500">
              {listFiltered ? t("evaluators.list.empty_hint_filtered") : t("evaluators.list.empty_hint")}
            </p>
          </div>
        ) : wide ? (
          <Table
            evaluators={rows}
            onViewEvaluator={handleViewEvaluator}
            sortedBy={{ column: currentSort.column, direction: currentSort.direction }}
          />
        ) : (
          <EvaluatorCards evaluators={rows} onViewEvaluator={handleViewEvaluator} />
        )}
      </div>

      {!failed && rows.length > 0 && (
        <div className="flex justify-center">
          <Pager page={page} pageCount={pageCount} onPage={pageFromBottom} disabled={loading} label={t("question_list.pagination_bottom")} />
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={handleCloseModal} evaluator={selectedEvaluator} />
    </div>
  );
}
